import { Event } from "interfaces/entities/event.model";
import { toDate } from "helpers/dateHelper";
import { Card, CardContent, Chip } from "@mui/material";
import { FaArrowRight } from "react-icons/fa";
import { useTranslation } from "react-i18next";

interface TicketEventItemProps {
  event: Event;
}

const Header = ({
  title,
  createdAt,
  actorName,
}: {
  title: string;
  createdAt: Date;
  actorName?: string;
}) => (
  <div className="flex items-center gap-2 flex-wrap">
    <strong>{title}</strong>
    {actorName ? <span className="text-sm text-gray-500">· {actorName}</span> : null}
    <span className="text-sm text-gray-500">· {toDate(createdAt)}</span>
  </div>
);

export const TicketEventItem = ({ event }: TicketEventItemProps) => {
  const { t } = useTranslation();
  if (!event) return null;

  const payload = event.payload || {};
  const actorName = event.actorName || payload.actorName;

  switch (event.eventType) {
    case "TICKET_CREATED":
      return (
        <Card>
          <CardContent>
            <Header title="Обращение создано" createdAt={event.createdAt} actorName={actorName} />
            {payload.subject ? <div className="py-2 text-sm">{payload.subject}</div> : null}
          </CardContent>
        </Card>
      );

    case "TICKET_STATUS_CHANGED":
      return (
        <Card>
          <CardContent>
            <Header title="Статус изменён" createdAt={event.createdAt} actorName={actorName} />
            <div className="py-2 flex items-center gap-2 flex-wrap">
              {payload.from ? (
                <Chip
                  size="small"
                  label={String(t(`ticket.status.${payload.from}`, { defaultValue: payload.from }))}
                />
              ) : (
                <span className="text-gray-400">—</span>
              )}
              <FaArrowRight className="text-gray-500" />
              <Chip
                size="small"
                color="primary"
                label={String(t(`ticket.status.${payload.to}`, { defaultValue: payload.to }))}
              />
            </div>
          </CardContent>
        </Card>
      );

    case "TICKET_PRIORITY_CHANGED":
      return (
        <Card>
          <CardContent>
            <Header title="Приоритет изменён" createdAt={event.createdAt} actorName={actorName} />
            <div className="py-2 flex items-center gap-2 flex-wrap">
              {payload.from ? (
                <Chip size="small" label={payload.from} />
              ) : (
                <span className="text-gray-400">—</span>
              )}
              <FaArrowRight className="text-gray-500" />
              <Chip size="small" color="primary" label={payload.to} />
            </div>
          </CardContent>
        </Card>
      );

    case "TICKET_TYPE_CHANGED":
      return (
        <Card>
          <CardContent>
            <Header
              title="Тип обращения изменён"
              createdAt={event.createdAt}
              actorName={actorName}
            />
            <div className="py-2 flex items-center gap-2 flex-wrap">
              {payload.from ? (
                <Chip size="small" label={payload.from} />
              ) : (
                <span className="text-gray-400">—</span>
              )}
              <FaArrowRight className="text-gray-500" />
              <Chip size="small" color="primary" label={payload.to} />
            </div>
          </CardContent>
        </Card>
      );

    case "TICKET_CATEGORY_CHANGED":
      return (
        <Card>
          <CardContent>
            <Header title="Категория изменена" createdAt={event.createdAt} actorName={actorName} />
            <div className="py-2 flex items-center gap-2 flex-wrap">
              <span>{payload.fromName || "—"}</span>
              <FaArrowRight className="text-gray-500" />
              <span>{payload.toName || "—"}</span>
            </div>
          </CardContent>
        </Card>
      );

    case "TICKET_BRANCH_CHANGED":
      return (
        <Card>
          <CardContent>
            <Header title="Филиал изменён" createdAt={event.createdAt} actorName={actorName} />
            <div className="py-2 flex items-center gap-2 flex-wrap">
              <span>{payload.fromName || "—"}</span>
              <FaArrowRight className="text-gray-500" />
              <span>{payload.toName || "—"}</span>
            </div>
          </CardContent>
        </Card>
      );

    case "TICKET_SUBJECT_CHANGED":
      return (
        <Card>
          <CardContent>
            <Header title="Тема изменена" createdAt={event.createdAt} actorName={actorName} />
            <div className="py-2 flex items-center gap-2 flex-wrap">
              <span className="text-gray-500">{payload.from || "—"}</span>
              <FaArrowRight className="text-gray-500" />
              <span>{payload.to}</span>
            </div>
          </CardContent>
        </Card>
      );

    case "TICKET_ASSIGNEE_CHANGED":
      return (
        <Card>
          <CardContent>
            <Header
              title="Назначен ответственный"
              createdAt={event.createdAt}
              actorName={actorName}
            />
            <div className="py-2 flex items-center gap-2 flex-wrap">
              <span>{payload.fromName || "—"}</span>
              <FaArrowRight className="text-gray-500" />
              <span>{payload.toName || "—"}</span>
            </div>
          </CardContent>
        </Card>
      );

    case "TICKET_CUSTOMER_CHANGED":
      return (
        <Card>
          <CardContent>
            <Header title="Клиент изменён" createdAt={event.createdAt} actorName={actorName} />
            <div className="py-2 flex items-center gap-2 flex-wrap">
              <span>{payload.fromName || "—"}</span>
              <FaArrowRight className="text-gray-500" />
              <span>{payload.toName || "—"}</span>
            </div>
          </CardContent>
        </Card>
      );

    case "TICKET_REPLY_SENT":
      return (
        <Card>
          <CardContent>
            <Header
              title="Ответ клиенту отправлен"
              createdAt={event.createdAt}
              actorName={actorName}
            />
            {payload.text ? (
              <div className="py-2 whitespace-pre-wrap text-sm">{payload.text}</div>
            ) : null}
          </CardContent>
        </Card>
      );

    case "TICKET_INTERNAL_NOTE":
      return (
        <Card sx={{ background: "#fff8e1" }}>
          <CardContent>
            <Header title="Внутренняя заметка" createdAt={event.createdAt} actorName={actorName} />
            {payload.text ? (
              <div className="py-2 whitespace-pre-wrap text-sm">{payload.text}</div>
            ) : null}
          </CardContent>
        </Card>
      );

    case "TICKET_RESOLVED":
      return (
        <Card>
          <CardContent>
            <Header title="Обращение решёно" createdAt={event.createdAt} actorName={actorName} />
          </CardContent>
        </Card>
      );

    case "TICKET_CLOSED": {
      const reasonLabel = payload.reason
        ? String(t(`ticket.closeReason.${payload.reason}`, { defaultValue: payload.reason }))
        : null;
      const title = payload.auto ? "Обращение закрыто автоматически" : "Обращение закрыто";
      return (
        <Card>
          <CardContent>
            <Header title={title} createdAt={event.createdAt} actorName={actorName} />
            {reasonLabel ? (
              <div className="py-2 flex items-center gap-2 flex-wrap">
                <span className="text-sm text-gray-500">Причина:</span>
                <Chip size="small" label={reasonLabel} />
              </div>
            ) : null}
          </CardContent>
        </Card>
      );
    }

    case "TICKET_REOPENED":
      return (
        <Card>
          <CardContent>
            <Header
              title="Обращение переоткрыт"
              createdAt={event.createdAt}
              actorName={actorName}
            />
            {payload.reason ? (
              <div className="py-2 text-sm text-gray-500">
                Был закрыт с причиной:{" "}
                {String(
                  t(`ticket.closeReason.${payload.reason}`, { defaultValue: payload.reason }),
                )}
              </div>
            ) : null}
          </CardContent>
        </Card>
      );

    case "COMMENT_ADDED":
      return (
        <Card>
          <CardContent>
            <Header title="Комментарий" createdAt={event.createdAt} actorName={actorName} />
            {payload.text ? (
              <div className="py-2 whitespace-pre-wrap text-sm">{payload.text}</div>
            ) : null}
          </CardContent>
        </Card>
      );

    default:
      return (
        <Card>
          <CardContent>
            <Header title={event.eventType} createdAt={event.createdAt} actorName={actorName} />
          </CardContent>
        </Card>
      );
  }
};
